/**
 * "Mein Kalender"-Tab für /mein-profil:
 * 
 *  1. Nächste Einsätze (Liste)
 *  2. Kalender-Abonnement-Karte (iCal-Token, Copy, Regenerate, Last-Created)
 *  
 * Komplette Self-Service-Sicht — der Mitarbeiter braucht den Admin nicht mehr,
 * um seine Einsatzplanung im privaten Kalender zu sehen.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Skeleton } from '../../components/ui/skeleton';
import { Alert, AlertDescription } from '../../components/ui/alert';
import {
  Calendar, CalendarDays, ArrowRight, Briefcase, Clock, MapPin, DoorOpen,
  Copy, Check, RefreshCw, Info, Apple, Mail, ExternalLink,
} from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { de } from 'date-fns/locale';
import api, { icalAPI } from '../../lib/api';
import { toast } from 'sonner';
import { MyCalendarMiniMap } from './MyCalendarMiniMap';

export const MyCalendarTab = () => {
  const [shifts, setShifts] = useState([]);
  const [loadingShifts, setLoadingShifts] = useState(true);

  const [tokenData, setTokenData] = useState(null);
  const [loadingToken, setLoadingToken] = useState(true);
  const [copied, setCopied] = useState(false);
  const [regenerating, setRegenerating] = useState(false);

  // Lade nächste 30 Tage Einsätze
  const loadShifts = useCallback(async () => {
    setLoadingShifts(true);
    try {
      const today = new Date();
      const in30 = new Date();
      in30.setDate(today.getDate() + 30);
      const { data } = await api.get('/shifts/my-shifts', {
        params: {
          start_date: format(today, 'yyyy-MM-dd'),
          end_date: format(in30, 'yyyy-MM-dd'),
        },
      });
      // Defensive: API kann Array oder {shifts:[]} liefern
      const list = Array.isArray(data) ? data : data.shifts || [];
      setShifts(list);
    } catch (err) {
      console.error('Einsätze konnten nicht geladen werden', err);
      setShifts([]);
    } finally {
      setLoadingShifts(false);
    }
  }, []);

  const loadToken = useCallback(async () => {
    setLoadingToken(true);
    try {
      const { data } = await icalAPI.getMyToken();
      setTokenData(data);
    } catch (err) {
      console.error('Token-Abruf fehlgeschlagen', err);
    } finally {
      setLoadingToken(false);
    }
  }, []);

  useEffect(() => {
    loadShifts();
    loadToken();
  }, [loadShifts, loadToken]);

  const fullUrl = tokenData?.path ? `${window.location.origin}${tokenData.path}` : '';
  const webcalUrl = fullUrl ? fullUrl.replace(/^https?:/, 'webcal:') : '';

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(fullUrl);
      setCopied(true);
      toast.success('Link kopiert');
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Konnte nicht kopieren');
    }
  };

  const handleRegenerate = async () => {
    if (!window.confirm(
      'Wirklich neuen Token erzeugen?\n\nAlle bestehenden Kalender-Abonnements werden sofort ungültig.'
    )) return;
    setRegenerating(true);
    try {
      const { data } = await icalAPI.regenerateMyToken();
      setTokenData(data);
      toast.success('Neuer Token erzeugt — bestehende Abonnements sind jetzt ungültig.');
    } catch (err) {
      console.error('Regenerate fehlgeschlagen', err);
      toast.error('Token konnte nicht erneuert werden.');
    } finally {
      setRegenerating(false);
    }
  };

  const upcomingShifts = shifts.slice(0, 8);

  return (
    <div className="space-y-6" data-testid="my-calendar-tab">
      {/* Nächste Einsätze */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-2">
              <Briefcase className="h-5 w-5 text-primary" />
              <CardTitle>Nächste Einsätze</CardTitle>
            </div>
            <Button asChild variant="outline" size="sm" data-testid="my-calendar-link-einsatzplan">
              <Link to="/einsatzplan">
                Vollständiger Einsatzplan
                <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
          </div>
          <CardDescription>Deine eigenen Einsätze in den nächsten 30 Tagen</CardDescription>
        </CardHeader>
        <CardContent>
          {loadingShifts ? (
            <div className="space-y-2">
              <Skeleton className="h-14 w-full" />
              <Skeleton className="h-14 w-full" />
              <Skeleton className="h-14 w-full" />
            </div>
          ) : upcomingShifts.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Briefcase className="h-12 w-12 mx-auto mb-2 opacity-40" />
              <p>Keine Einsätze in den nächsten 30 Tagen</p>
            </div>
          ) : (
            <div className="space-y-2" data-testid="my-calendar-upcoming-list">
              {upcomingShifts.map((shift) => (
                <div
                  key={shift.id}
                  className="flex items-center gap-3 p-3 rounded-lg bg-muted/30 hover:bg-muted/50 transition-colors"
                  data-testid={`my-calendar-shift-${shift.id}`}
                >
                  <div
                    className="w-1.5 h-12 rounded-full flex-shrink-0"
                    style={{ backgroundColor: shift.massnahme_color || '#6B7280' }}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium">
                        {format(parseISO(shift.date), 'EEEE, dd.MM.yyyy', { locale: de })}
                      </span>
                      {shift.massnahme_short_code && (
                        <Badge
                          variant="outline"
                          className="text-xs"
                          style={{ borderColor: shift.massnahme_color, color: shift.massnahme_color }}
                        >
                          {shift.massnahme_short_code}
                        </Badge>
                      )}
                    </div>
                    <div className="flex items-center gap-3 text-sm text-muted-foreground mt-0.5 flex-wrap">
                      <span className="flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        {shift.start_time && shift.end_time ? `${shift.start_time} – ${shift.end_time}` : 'Ganztags'}
                      </span>
                      {shift.massnahme_name && <span className="truncate">{shift.massnahme_name}</span>}
                      {shift.room_name && (
                        <span className="flex items-center gap-1">
                          <DoorOpen className="h-3 w-3" />
                          {shift.room_number ? `${shift.room_number} – ${shift.room_name}` : shift.room_name}
                        </span>
                      )}
                      {shift.location_name && (
                        <span className="flex items-center gap-1">
                          <MapPin className="h-3 w-3" />
                          {shift.location_name}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
              {shifts.length > upcomingShifts.length && (
                <p className="text-xs text-muted-foreground text-center pt-2">
                  + {shifts.length - upcomingShifts.length} weitere Einsätze in den nächsten 30 Tagen
                </p>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Monats-Mini-Map */}
      <MyCalendarMiniMap />

      {/* Kalender-Abonnement */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <CalendarDays className="h-5 w-5 text-primary" />
            <CardTitle>Kalender-Abonnement</CardTitle>
          </div>
          <CardDescription>
            Abonniere deine Einsätze, Urlaube und Abwesenheiten in Outlook, Apple oder Google
            Kalender — der Kalender aktualisiert sich automatisch.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="my-cal-url">Persönlicher Subscription-Link</Label>
            {loadingToken ? (
              <Skeleton className="h-10 w-full" />
            ) : (
              <div className="flex gap-2">
                <Input
                  id="my-cal-url"
                  value={fullUrl}
                  readOnly
                  className="font-mono text-xs"
                  data-testid="my-calendar-ical-url"
                />
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  onClick={handleCopy}
                  disabled={!fullUrl}
                  data-testid="my-calendar-copy-button"
                  title="In Zwischenablage kopieren"
                >
                  {copied ? <Check className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4" />}
                </Button>
              </div>
            )}
            <p className="text-xs text-muted-foreground flex items-start gap-1.5">
              <Info className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
              Der Link enthält einen geheimen Token — niemandem weitergeben. Wer den Link hat,
              sieht deine Einsätze.
            </p>
            {tokenData?.created_at && (
              <p className="text-xs text-muted-foreground">
                Erzeugt am {format(parseISO(tokenData.created_at), 'dd.MM.yyyy, HH:mm', { locale: de })} Uhr
              </p>
            )}
          </div>

          {/* Quick-Actions */}
          <div className="flex flex-wrap gap-2">
            {webcalUrl && (
              <Button asChild variant="outline" size="sm" data-testid="my-calendar-apple-direct">
                <a href={webcalUrl}>
                  <Apple className="h-4 w-4 mr-2" />
                  Apple Kalender
                </a>
              </Button>
            )}
            <Button asChild variant="outline" size="sm">
              <a href="https://outlook.live.com/calendar/" target="_blank" rel="noreferrer" data-testid="my-calendar-outlook-link">
                <Mail className="h-4 w-4 mr-2" />
                Outlook öffnen
                <ExternalLink className="h-3 w-3 ml-1" />
              </a>
            </Button>
            <Button asChild variant="outline" size="sm">
              <a href="https://calendar.google.com/" target="_blank" rel="noreferrer" data-testid="my-calendar-google-link">
                <Calendar className="h-4 w-4 mr-2" />
                Google Kalender öffnen
                <ExternalLink className="h-3 w-3 ml-1" />
              </a>
            </Button>
          </div>

          <Alert className="bg-amber-50 dark:bg-amber-950/30 border-amber-200 dark:border-amber-900">
            <Info className="h-4 w-4 text-amber-600" />
            <AlertDescription className="text-xs">
              Der Kalender enthält Einsätze, genehmigte Urlaube, Krankheits- und Elternzeiten von
              <strong> 30 Tagen rückwirkend</strong> bis <strong>12 Monate in der Zukunft</strong>.
              Anleitungen zur Einrichtung findest du im{' '}
              <Link to="/einsatzplan" className="underline">Einsatzplan unter „Kalender abonnieren"</Link>.
            </AlertDescription>
          </Alert>

          <div className="pt-2 border-t">
            <Button
              variant="outline"
              size="sm"
              onClick={handleRegenerate}
              disabled={regenerating || loadingToken}
              data-testid="my-calendar-regenerate-button"
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${regenerating ? 'animate-spin' : ''}`} />
              Neuen Link erzeugen
            </Button>
            <p className="text-xs text-muted-foreground mt-2">
              Verwende dies, falls dein Link versehentlich geteilt wurde. Alle bestehenden
              Kalender-Abonnements werden sofort ungültig.
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
